import React, { useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Menu, Skeleton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import { useMetaAdsContext } from '../MetaAdsContext';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import {
  appModalCloseButtonSx, appModalHeaderDividerSx, appModalPaperSx, appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { getUnitReport } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { META_DEFAULT_PERIOD_PRESET, metaPeriodPresetRange } from '../metaPeriod';
import { readUnitReport } from '../metaUnitReport';
import { MetaBlockState } from './MetaDataParts';
import MetaPeriodSelect from './MetaPeriodSelect';
import UnitReportDocument from './UnitReportDocument';
import { useUnitReportPrint } from './UnitReportPrint';
import { useUnitReportShareLink } from './UnitReportShares';
import { metaAdsMutedTextSx, metaAdsSkeletonSx } from './metaAdsUi.styles';

// Misma tarjeta y mosaico que `UnitReportDocument` (tono oscuro): el placeholder no salta al resolver.
const reportCardSx = {
  border: '1px solid rgba(255,255,255,0.1)', bgcolor: '#262626', borderRadius: '10px', p: 2,
  display: 'flex', flexDirection: 'column', gap: 1.25,
};
const reportTileSx = {
  border: '1px solid rgba(255,255,255,0.1)', bgcolor: '#303030', borderRadius: '8px', px: 1.5, py: 1.1,
};
const bar = (height, width, extra) => (
  <Skeleton variant="rounded" height={height} width={width} sx={{ ...metaAdsSkeletonSx, ...extra }} />
);

/** Placeholder del documento: encabezado, resumen, gasto, semanas y anuncios. */
function UnitReportSkeleton() {
  return (
    <Box aria-busy="true" data-testid="unit-report-loading" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, mb: 0.5 }}>
          {bar(22, 72)}
          {bar(12, 168)}
        </Box>
        {bar(20, '46%')}
        {bar(13, '34%')}
        {bar(13, '78%')}
      </Box>

      <Box sx={reportCardSx}>
        {bar(15, 72)}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
          {['58%', '72%', '48%', '64%', '70%', '54%'].map((w) => (
            <Box key={w} data-testid="unit-report-skel-tile" sx={reportTileSx}>
              {bar(20, w)}
              {bar(12, '78%', { mt: 0.75 })}
            </Box>
          ))}
        </Box>
        {bar(12, '42%')}
      </Box>

      <Box sx={reportCardSx}>
        {bar(15, 128)}
        {['40%', '52%', '36%', '44%', '48%'].map((w) => (
          <Box
            key={w}
            data-testid="unit-report-skel-row"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.7, borderBottom: '1px solid rgba(255,255,255,0.1)' }}
          >
            {bar(13, w)}
            {bar(13, 72)}
          </Box>
        ))}
      </Box>

      <Box sx={reportCardSx}>
        {bar(15, 168)}
        {['78%', '42%', '90%', '28%'].map((w) => (
          <Box key={w} sx={{ display: 'grid', gridTemplateColumns: '96px minmax(40px, 1fr) 72px', columnGap: 1.25, alignItems: 'center' }}>
            {bar(13, '100%')}
            {bar(8, w)}
            {bar(13, '100%')}
          </Box>
        ))}
      </Box>

      <Box sx={reportCardSx}>
        {bar(15, 196)}
        {[0, 1].map((i) => (
          <Box key={i} data-testid="unit-report-skel-ad" sx={{ display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr)', columnGap: 1.25, alignItems: 'start' }}>
            {bar(48, 48, { borderRadius: '6px' })}
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {bar(14, i ? '58%' : '72%')}
              {bar(12, '46%')}
              {bar(12, '84%')}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ⋮ del informe (t1059u / t1062u): una línea por opción; ancho como el ⋮ de campañas (MetaActionMenu).
const reportMenuPaperProps = {
  ...ACTION_MENU_PAPER_PROPS,
  sx: { ...ACTION_MENU_PAPER_PROPS.sx, width: 'auto', minWidth: '164px', maxWidth: 'min(340px, 92vw)' },
};

/**
 * «Ver informe» de una propiedad (desde la ficha o la pestaña Informe): vista previa
 * con el mismo documento que se imprime / se comparte y período elegible. ⋮ junto a la ✕ (t1059u):
 * «Descargar o imprimir» y «Copiar link» (solo dueño o editor: `viewerCanLink`).
 * Sin sección de compartir ni pie: se cierra con la ✕, Esc o clic afuera. Siempre con gasto y costos (sin casilla para ocultarlos).
 *
 * @param {{ itemKind: string, recordId: string, unitLabel?: string|null, ownerUserId?: string|null,
 *   initialPreset?: string, onClose: () => void }} props
 */
export default function UnitReportDialog({
  itemKind, recordId, unitLabel = null, ownerUserId = null, initialPreset = META_DEFAULT_PERIOD_PRESET, onClose,
}) {
  const [preset, setPreset] = useState(initialPreset);
  const period = useMemo(() => metaPeriodPresetRange(preset), [preset]);
  const read = useMetaRead(
    (signal) => getUnitReport({ itemKind, id: recordId, period, ownerUserId, signal }),
    { deps: [itemKind, recordId, ownerUserId, period.since, period.until] },
  );
  const report = useMemo(() => (read.status === 'ready' ? readUnitReport(read.res) : null), [read.status, read.res]);
  const { print, printNode } = useUnitReportPrint(report);
  const title = unitLabel || report?.unit?.label || 'la propiedad';
  const meta = useMetaAdsContext();
  // Crear links: dueño o editor (`resolve_link_owner` = `viewer_can_link`). Fuera de Campañas, sin opción.
  const canShare = Boolean(meta?.metaConnection?.connection?.viewerCanLink) && read.status !== 'unavailable';
  const { copyLink, copying } = useUnitReportShareLink({ itemKind, recordId, ownerUserId, period });
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { ...appModalPaperSx, width: 'min(720px, 96vw)', maxWidth: '720px', height: 'min(860px, 92vh)', maxHeight: '92vh' },
        'data-testid': 'unit-report-dialog',
      }}
      aria-labelledby="unit-report-dialog-title"
    >
      <DialogTitle id="unit-report-dialog-title" component="div" sx={appModalTitleBarSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ ...appModalTitleSx, fontSize: '18px', textTransform: 'none', letterSpacing: 0, overflowWrap: 'anywhere' }}>
            {`Informe de anuncios · ${title}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={<span>Más opciones</span>} placement="bottom" arrow>
            <IconButton
              size="small"
              aria-label="Más opciones del informe"
              aria-haspopup="menu"
              aria-expanded={menuAnchor ? 'true' : undefined}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={appModalCloseButtonSx}
              data-testid="unit-report-menu"
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} aria-label="Cerrar" sx={appModalCloseButtonSx}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={reportMenuPaperProps}
          MenuListProps={{ ...ACTION_MENU_LIST_PROPS, 'aria-label': 'Opciones del informe' }}
        >
          <ActionMenuItem
            icon={<PrintOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Descargar o imprimir"
            disabled={!report}
            testId="unit-report-menu-print"
            onClick={() => { setMenuAnchor(null); print(); }}
          />
          {canShare ? (
            <ActionMenuItem
              icon={<LinkIcon sx={{ fontSize: 18 }} />}
              label="Copiar link"
              loading={copying}
              disabled={copying}
              testId="unit-report-menu-copy-link"
              // Sin cerrar antes de llamar: el portapapeles necesita el gesto de este clic.
              onClick={() => { copyLink(); setMenuAnchor(null); }}
            />
          ) : null}
        </Menu>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ ...DOCK_SCROLLBAR_SX, px: '20px', pt: 2, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <MetaPeriodSelect value={preset} onChange={setPreset} testId="unit-report-period" />
          <Typography sx={metaAdsMutedTextSx}>El informe incluye el gasto y los costos.</Typography>
        </Box>

        {read.status === 'loading' ? <UnitReportSkeleton /> : null}
        {read.status === 'unavailable' ? (
          <MetaBlockState status="unavailable" testId="unit-report-state" unavailableText="El informe todavía no está disponible en este entorno." />
        ) : null}
        {read.status === 'error' ? (
          <MetaBlockState status="error" error={read.error} onRetry={read.reload} testId="unit-report-state" />
        ) : null}
        {report ? <UnitReportDocument report={report} tone="dark" /> : null}
        {read.status === 'ready' && !report ? (
          <MetaBlockState status="unavailable" testId="unit-report-state" unavailableText="Todavía no hay datos para el informe." />
        ) : null}

      </DialogContent>
      {printNode}
    </Dialog>
  );
}
